<template>
    <vue-splitter :isHorizontal="true" @update-percent="onChangeSplitter">
      <template #top-pane>
        <NasimMap @no-geometry="showHinweis = 'Hinweis: Für das ausgewählte Gewässer fehlt die Georeferenzierung.'" 
                  @gewaesser-clicked="gewaesserSelection" 
                  @click-in-map="randomClickInMapListener"
                  @gw-hover="drawPointInLS"
                  @gw-click-marker="drawStaticPointInLS"
                  @available-layers="availableLayersCallback"
                  :layer-visibility="layerVisibility"
                  :abfluss="abfluss"
                  :wvconfig="wvconfig"
                  :backgroundlayers="backgroundlayers"
                  :selectedBackgroundLayer="selectedBackgroundLayer"
                  :additionalLayers="additionalLayers"
                  :toggleAdditionalLayer="toggleAdditionalLayer"
                  :selectedData="selectedData"/>
      </template>
      <template #bottom-pane >
        <LaengsSchnitt ref="laengsschnitt"
                       @gewaesser-changed="gewaesserSelection"
                       @line-hover="getCoordinateAtM" 
                       @no-l-s="showHinweis = 'Hinweis: Für das ausgewählte Gewässer sind nicht genügend Daten vorhanden, um einen Längsschnitt darzustellen.'"
                       @line-click="setGwKmMarker"
                       @abfluss="setAbfluss"
                       :selectedData="selectedData"/>
      </template>
    </vue-splitter>
    <div v-if="showHinweis" class="notification" v-text="showHinweis"></div>
    <OptionsMenu :wvconfig="wvconfig"
                 :backgroundlayers="backgroundlayers"
                 :additionalLayers="additionalLayers"
                 :availableLayers="availableLayersRef"
                 @layer-visibility="toggleLayerVisibility"
                 @selected-background-layer="selectBackgroundLayer"
                 @toggle-additional-layer="toggleAdditionalLayerFunc"/>
</template>

<script lang="ts">
import NasimMap from './components/NasimMap.vue';
import LaengsSchnitt from './components/LaengsSchnitt.vue';
import OptionsMenu from './components/OptionsMenu.vue';
import { ref, shallowRef, watch } from 'vue';
import type { Ref } from 'vue';
import * as baseConfig  from "../src/js/baseConfig";
import { registerBackgroundLayers, registerAdditionalLayers } from "mapview-gui-utils/src/BackgroundLayers";
import { config as wvconfig } from "./config/WebviewerConfig";
import { silentapifetch } from "vueforms/src/utils/apifetch";
import MapViewTour from "./NasimMapviewtour";
import markdownParser from "mapview-gui-utils/src/markdownParserInstance";
import VueSplitter  from 'vueforms/src/components/Splitter.vue';
import { SelectedData } from './components/SelectedData';

export default {
  name: 'App',
  components: {
    NasimMap,
    LaengsSchnitt,
    OptionsMenu,
    VueSplitter 
  },
  setup() {
    const baseurl = baseConfig.getBaseURL() + "modeldata/";
    const showHinweis : Ref<Boolean | String> = ref(false);
    const layerVisibility : Ref<Record<string, boolean | string>> = ref({});
    const abfluss = ref();
    const backgroundlayers = shallowRef();
    const additionalLayers = shallowRef(); //KML/KMZ Dateien
    const selectedBackgroundLayer = ref();
    const toggleAdditionalLayer = ref();
    const availableLayersRef = ref([]);
    const laengsschnitt = ref();
    const selectedData = new SelectedData();
    
    silentapifetch(baseurl + "metamodel").then((response) => 
    response.json()).then(async metamodel => {
      await wvconfig.setConfig(metamodel.config);
      backgroundlayers.value = await Promise.all(registerBackgroundLayers(wvconfig));
    }).catch((e) => {
      alert(`Modell konnte nicht geladen werden.`);
      console.error(e);
    }).then(async () => {
      additionalLayers.value = await Promise.all(registerAdditionalLayers(wvconfig));
    }).catch((e) => {
      alert(`KML/KMZ Dateien konnten nicht geladen werden.`);
      console.error(e);
    }).then(() => {
      if (wvconfig.config.value.introMessage) {
        MapViewTour.steps[0].text = markdownParser.render(wvconfig.config.value.introMessage);
      }
    }).then(() => {
      if (!MapViewTour.getTourWasPlayed())
      MapViewTour.start();
    });


    const availableLayersCallback = (availableLayers: Array<number>) => {
      availableLayersRef.value = availableLayers;
    }

    const gewaesserSelection = (gewaesser: string) => {
      showHinweis.value = false;
      selectedData.setGewaesser(gewaesser);
    }

    const randomClickInMapListener = () => {
      showHinweis.value = false;
      selectedData.setGewaesser(null);
    }

    const getCoordinateAtM = (x: number) => {
      selectedData.setMovingKm(x);
    }

    const drawPointInLS = (x: number | undefined) => {
      if (x) {
        selectedData.setMovingKm(x/1000);
      } else {
        selectedData.setMovingKm(undefined);
      }
    }

    const drawStaticPointInLS = (x: number | undefined) => {
      if (x) {
        selectedData.setFixedKm({km: x/1000});
      } else {
        selectedData.setFixedKm(undefined);
      }
    }

    const setGwKmMarker = (x: number, abfluss: object) => {
      selectedData.setFixedKm({km: x, abfluss: abfluss});
      //xValueFromMapForStaticMarker.value = x; //Längsschnitt setzt in diesem Fall (Klick in LS) diesen wert selbst PROBLEM Abfluss oll dann nicht ermittelt werden, wenn km von LS kommt
    }

    const setAbfluss = (param: object) => abfluss.value = param;

    const toggleLayerVisibility = (obj : Ref<Record<string, boolean | string>>) => {
      layerVisibility.value = obj;
    }

    const selectBackgroundLayer = (layer) => {
      selectedBackgroundLayer.value = layer;
    }

    const toggleAdditionalLayerFunc = (obj) => {
      toggleAdditionalLayer.value = obj;
    }

    const onChangeSplitter = () => {
      if (laengsschnitt.value) laengsschnitt.value?.adjustSize();
    } 

    watch(() => MapViewTour.triggerLaengstesGewaesser.value, (newVal) => { 
      if (newVal) {
        selectedData.setGewaesser(MapViewTour.getLaengstesGewaesser());
      }
    }, { deep: true });

    return {
      baseurl,
      gewaesserSelection,
      showHinweis,
      randomClickInMapListener,
      getCoordinateAtM,
      drawPointInLS,
      drawStaticPointInLS,
      setGwKmMarker,
      toggleLayerVisibility,
      layerVisibility,
      setAbfluss,
      abfluss,
      wvconfig,
      backgroundlayers,
      selectBackgroundLayer,
      selectedBackgroundLayer,
      additionalLayers,
      toggleAdditionalLayer,
      toggleAdditionalLayerFunc,
      availableLayersCallback,
      availableLayersRef,
      laengsschnitt,
      onChangeSplitter,
      selectedData
    }
  }
}
</script>
 
<style>
</style>